import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { FeatureAccess, FeatureAccessRequest } from '@hria/types';
import { sdk } from '../lib/sdk';
import { UsersAPI } from '../lib/sdk/users';
import { useAuthStore } from './authStore';
import { getErrorMessage } from '../lib/utils/errorMessage';

interface FeatureAccessState {
  // Features for the CURRENT logged-in user (affects navigation)
  currentFeatures: FeatureAccess | null;
  // Features for the INSPECTED user in Feature Access page
  inspectedFeatures: FeatureAccess | null;
  isLoading: boolean;
  error: string | null;
  fetchCurrentUserFeatures: () => Promise<void>;
  fetchFeaturesByEmail: (userEmail: string) => Promise<void>;
  updateFeatureAccess: (request: FeatureAccessRequest) => Promise<boolean>;
  hasFeature: (feature: keyof FeatureAccess['features']) => boolean;
  clearFeatures: () => void;
}

export const useFeatureAccessStore = create<FeatureAccessState>()(
  persist(
    (set, get) => ({
      currentFeatures: null,
      inspectedFeatures: null,
      isLoading: false,
      error: null,

      fetchCurrentUserFeatures: async () => {
        set({ isLoading: true, error: null });
        try {
          const { user } = useAuthStore.getState();
          if (!user?.email) {
            set({ isLoading: false });
            return;
          }
          // Use profile endpoint which is accessible to all authenticated users
          const profileResponse = await UsersAPI.getProfile();
          const target = profileResponse.user;
          if (!target) {
            set({ currentFeatures: null, isLoading: false });
            return;
          }
          const features = (target.features as any) || {};
          const resolved: FeatureAccess = {
            id: target._id || target.id,
            userId: target._id || target.id,
            userEmail: target.email,
            features: {
              manageUsers: !!features.manageUsers,
              manageDonors: !!features.manageDonors,
              viewAccounts: !!features.viewAccounts,
              manageAccounts: !!features.manageAccounts,
              accessReports: !!features.accessReports,
              manageDonationTypes: !!features.manageDonationTypes,
              manageExpenseTypes: !!features.manageExpenseTypes,
              // Income module features
              manageIncomeDepartments: !!features.manageIncomeDepartments,
              manageIncomeTypes: !!features.manageIncomeTypes,
              addIncome: !!features.addIncome,
              manageIncomes: !!features.manageIncomes,
              // Separate Add and Manage features
              addDonation: !!features.addDonation,
              manageDonations: !!features.manageDonations,
              addExpense: !!features.addExpense,
              manageExpenses: !!features.manageExpenses,
              // Separate View and Manage features for administrative entities
              viewRegions: !!features.viewRegions,
              manageRegions: !!features.manageRegions,
              viewOffices: !!features.viewOffices,
              manageOffices: !!features.manageOffices,
              viewControllers: !!features.viewControllers,
              manageControllers: !!features.manageControllers,
              manageBackupAutomation: !!features.manageBackupAutomation,
              manageScheduledReports: !!features.manageScheduledReports,
              viewAutomationHistory: !!features.viewAutomationHistory,
            },
            grantedBy: 'system',
            grantedAt: new Date(),
            updatedAt: new Date(),
          };
          set({ currentFeatures: resolved, isLoading: false });
        } catch (error: any) {
          set({ isLoading: false, error: getErrorMessage(error, 'Failed to fetch features') });
        }
      },

      fetchFeaturesByEmail: async (userEmail: string) => {
        set({ isLoading: true, error: null });
        try {
          const list = await UsersAPI.list({ search: userEmail, page: 1, limit: 1 });
          const target = list.users?.[0];
          if (!target) {
            set({ inspectedFeatures: null, isLoading: false });
            return;
          }
          const features = (target.features as any) || {};
          const resolved: FeatureAccess = {
            id: target._id,
            userId: target._id,
            userEmail: target.email,
            features: {
              manageUsers: !!features.manageUsers,
              manageDonors: !!features.manageDonors,
              viewAccounts: !!features.viewAccounts,
              manageAccounts: !!features.manageAccounts,
              accessReports: !!features.accessReports,
              manageDonationTypes: !!features.manageDonationTypes,
              manageExpenseTypes: !!features.manageExpenseTypes,
              // Income module features
              manageIncomeDepartments: !!features.manageIncomeDepartments,
              manageIncomeTypes: !!features.manageIncomeTypes,
              addIncome: !!features.addIncome,
              manageIncomes: !!features.manageIncomes,
              // Separate Add and Manage features
              addDonation: !!features.addDonation,
              manageDonations: !!features.manageDonations,
              addExpense: !!features.addExpense,
              manageExpenses: !!features.manageExpenses,
              // Separate View and Manage features for administrative entities
              viewRegions: !!features.viewRegions,
              manageRegions: !!features.manageRegions,
              viewOffices: !!features.viewOffices,
              manageOffices: !!features.manageOffices,
              viewControllers: !!features.viewControllers,
              manageControllers: !!features.manageControllers,
              manageBackupAutomation: !!features.manageBackupAutomation,
              manageScheduledReports: !!features.manageScheduledReports,
              viewAutomationHistory: !!features.viewAutomationHistory,
            },
            grantedBy: 'system',
            grantedAt: new Date(),
            updatedAt: new Date(),
          };
          set({ inspectedFeatures: resolved, isLoading: false });
        } catch (error: any) {
          set({ isLoading: false, error: getErrorMessage(error, 'Failed to fetch features') });
        }
      },

      updateFeatureAccess: async (request: FeatureAccessRequest) => {
        set({ isLoading: true, error: null });
        try {
          // Find target by email, then update their features
          const list = await UsersAPI.list({ search: request.userEmail, page: 1, limit: 1 });
          const target = list.users?.[0];
          if (!target) throw new Error('User not found');
          await sdk.users.updateFeatures(target._id, request.features as any);
          // Refresh inspected features
          await get().fetchFeaturesByEmail(request.userEmail);
          // If updating self, also refresh current features
          const { user } = useAuthStore.getState();
          if (user?.email && user.email.toLowerCase() === request.userEmail.toLowerCase()) {
            await get().fetchCurrentUserFeatures();
          }
          set({ isLoading: false });
          return true;
        } catch (error: any) {
          set({
            isLoading: false,
            error: getErrorMessage(error, 'Failed to update features'),
          });
          return false;
        }
      },

      hasFeature: (feature: keyof FeatureAccess['features']) => {
        const { user } = useAuthStore.getState();
        if (user?.role === 'SuperAdmin') return true;
        const { currentFeatures } = get();
        if (currentFeatures?.features[feature]) return true;
        // "manage" access is a superset of "view" access for Accounts.
        if (feature === 'viewAccounts' && currentFeatures?.features.manageAccounts) {
          return true;
        }
        return false;
      },

      clearFeatures: () => {
        set({ currentFeatures: null, inspectedFeatures: null, error: null });
      },
    }),
    {
      name: 'hria-feature-access',
      partialize: (state) => ({ 
        currentFeatures: state.currentFeatures
      }),
    }
  )
);
